import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Plus } from 'lucide-react'
import { useClickOutside } from '@/lib/useClickOutside'
import { QUICK_TOKENS, TOKEN_LABELS, customToken } from '@/features/messaging/tokens'
import { useFieldDefinitions } from '@/features/contacts/useContacts'
import { cn } from '@/lib/cn'

const MENU_WIDTH = 256

/** Which built-in tokens belong to which group of the menu. */
const GROUPS: { title: string; tokens: string[] }[] = [
  { title: 'Client', tokens: ['{{first_name}}', '{{last_name}}', '{{full_name}}', '{{email}}', '{{mobile}}'] },
  { title: 'Property / deal', tokens: ['{{deal_title}}'] },
  { title: 'Me', tokens: ['{{agent_first_name}}', '{{agent_last_name}}', '{{agent_name}}', '{{agent_email}}', '{{agent_phone}}', '{{agent_signature}}'] },
]

/**
 * Personalise a message in one click: the fields used most as buttons ("First name", "Property",
 * "My name"), and "Insert ▾" for the rest — in words, grouped, including the agency's custom fields.
 * Inserts the token at the cursor. The menu is portalled so a card's edge can't clip it.
 */
export default function InsertTokenMenu({
  tokens,
  onInsert,
  tone = 'default',
  customFields = ['contact'],
}: {
  /** The built-in tokens this message can use (e.g. no signature in an SMS). */
  tokens: string[]
  onInsert: (token: string) => void
  tone?: 'default' | 'onDark'
  /** Whose custom fields to offer: a deal message can use both, a contact or list send only the contact's. */
  customFields?: ('contact' | 'deal')[]
}) {
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const open = pos != null
  const close = () => setPos(null)
  useClickOutside(menuRef, () => close(), open)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const { data: contactDefs } = useFieldDefinitions('contact')
  const { data: dealDefs } = useFieldDefinitions('deal')
  const custom = [
    ...(customFields.includes('contact') ? (contactDefs ?? []).map((d) => ({ d, entity: 'contact' as const })) : []),
    ...(customFields.includes('deal') ? (dealDefs ?? []).map((d) => ({ d, entity: 'deal' as const })) : []),
  ].filter(({ d }) => d.dataType !== 'file')

  if (tokens.length === 0) return null
  const available = new Set(tokens)
  const quick = QUICK_TOKENS.filter((q) => available.has(q.token))
  const groups = GROUPS.map((g) => ({ ...g, tokens: g.tokens.filter((t) => available.has(t)) })).filter((g) => g.tokens.length)

  function insert(token: string) {
    onInsert(token)
    close()
  }

  function toggle() {
    if (open) return close()
    const r = buttonRef.current?.getBoundingClientRect()
    if (!r) return
    const roomBelow = window.innerHeight - r.bottom - 12
    const below = roomBelow > 240
    setPos({
      top: below ? r.bottom + 4 : 8,
      left: Math.max(8, Math.min(r.left, window.innerWidth - MENU_WIDTH - 8)),
      maxHeight: below ? roomBelow : r.top - 16,
    })
  }

  const chip = cn(
    'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border',
    tone === 'onDark' ? 'border-white/30 text-white hover:bg-white/10' : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-brand-primary',
  )
  const item = 'w-full text-left px-3 py-1.5 text-sm text-brand-dark hover:bg-gray-50 rounded-lg'
  const heading = 'px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400'

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {quick.map((q) => (
        <button
          key={q.token}
          type="button"
          // keep the editor's selection: don't take focus on press
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onInsert(q.token)}
          title={`Insert ${TOKEN_LABELS[q.token] ?? q.label}`}
          className={chip}
        >
          <Plus size={11} /> {q.label}
        </button>
      ))}
      <button
        ref={buttonRef}
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        className={chip}
      >
        Insert <ChevronDown size={12} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH, maxHeight: pos.maxHeight }}
            className="fixed z-[60] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg p-1"
          >
            {groups.map((g) => (
              <div key={g.title}>
                <div className={heading}>{g.title}</div>
                {g.tokens.map((t) => (
                  <button key={t} type="button" role="menuitem" onMouseDown={(e) => e.preventDefault()} onClick={() => insert(t)} className={item}>
                    {TOKEN_LABELS[t] ?? t}
                  </button>
                ))}
              </div>
            ))}
            {custom.length > 0 && (
              <div>
                <div className={heading}>Custom fields</div>
                {custom.map(({ d, entity }) => (
                  <button
                    key={`${entity}.${d.fieldKey}`}
                    type="button"
                    role="menuitem"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insert(customToken(entity, d.fieldKey))}
                    className={item}
                  >
                    {d.label}
                    {customFields.length > 1 && <span className="text-gray-400"> · {entity === 'deal' ? 'deal' : 'client'}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}
